import { LOCALE } from '@/utils/constants';

/**
 * GA4 event tracking through the GTM dataLayer.
 *
 * The code only pushes events GTM cannot infer on its own (a form accepted by the API, the chat
 * opened, the quiz started). Everything derivable from clicks and URLs (scroll depth, outbound
 * links, file downloads) is configured in the GTM container, not here. GTM maps each event name
 * below to a GA4 Event tag; the same names land in BigQuery as `event_name`, and the parameters
 * as `event_params`, so the search-warehouse views read them without any mapping.
 *
 * Every push carries `page_path` (locale prefix stripped) and `page_type` (the same content
 * classification the BigQuery staging views derive from the URL), so "on which page did this
 * happen" is answerable in GA4 reports without registering the raw URL as a dimension.
 */

type PageType =
  | 'home'
  | 'blog'
  | 'glossary'
  | 'case_study'
  | 'library'
  | 'directory'
  | 'listing'
  | 'utility'
  | 'service_page';

export type AnalyticsEvent =
  | { event: 'ai_chat_open' }
  | { event: 'ai_chat_message_sent' }
  | { event: 'ai_chat_services_shown' }
  | { event: 'ai_chat_service_picked'; service: string }
  | { event: 'ai_chat_lead_form_shown'; lead_source: 'agent' | 'header' }
  | { event: 'ai_chat_lead_submit'; lead_source: 'agent' | 'header'; service?: string }
  | { event: 'ai_voice_start' }
  | { event: 'ai_voice_end'; duration_sec: number; end_reason: 'user' | 'agent' | 'error' | 'limit' }
  | { event: 'ai_voice_error'; error_type: 'mic_denied' | 'session' | 'connection' | 'rate_limit' }
  | { event: 'quiz_start'; quiz_locale: string }
  | { event: 'quiz_complete'; quiz_locale: string; quiz_score: number; quiz_band: string }
  | {
      event: 'contact_form_submit';
      form_id: 'full' | 'compact' | 'inline';
      form_locale: string;
      purpose?: string;
    };

type PageContext = { page_path: string; page_type: PageType };

const LOCALE_PREFIX = new RegExp(`^/(${Object.values(LOCALE).join('|')})(?=/|$)`);

const LISTING_PATHS = new Set(['/blog', '/glossary', '/case-studies', '/library', '/directory']);
const UTILITY_PATHS = new Set(['/contact', '/thanks', '/privacy-policy', '/about-us', '/404']);

export const getPageType = (canonicalPath: string): PageType => {
  if (canonicalPath === '/') return 'home';
  if (canonicalPath.startsWith('/blog/')) return 'blog';
  if (canonicalPath.startsWith('/glossary/')) return 'glossary';
  if (canonicalPath.startsWith('/case-studies/')) return 'case_study';
  if (canonicalPath.startsWith('/library/')) return 'library';
  if (canonicalPath.startsWith('/directory/')) return 'directory';
  if (LISTING_PATHS.has(canonicalPath)) return 'listing';
  if (UTILITY_PATHS.has(canonicalPath)) return 'utility';
  return 'service_page';
};

const getPageContext = (): PageContext => {
  const rawPath = window.location.pathname.replace(/\/$/, '') || '/';
  const canonicalPath = rawPath.replace(LOCALE_PREFIX, '') || '/';
  return { page_path: canonicalPath, page_type: getPageType(canonicalPath) };
};

export const trackEvent = (payload: AnalyticsEvent): void => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ...getPageContext(), ...payload });
};
